import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { BadgeCheck } from 'lucide-react';
import { ContactForm } from '../components/ContactForm';
import { APP_CONFIG } from '../config/app';
import { BUSINESS } from '../config/business';
import { useSeoMeta } from '../hooks/useSeoMeta';

import TrackedContactLink from '../components/TrackedContactLink';
const WHATSAPP_URL = APP_CONFIG.contact.whatsappUrl;

const WhatsAppIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const ContactPage: React.FC = () => {
  useSeoMeta({
    title: 'Contact – SOS IT Services, Viry (74580)',
    description: `Contactez SOS IT Services, basé à ${BUSINESS.address.city} (${BUSINESS.address.postalCode}) : tél. ${BUSINESS.phoneDisplay}, ${BUSINESS.hours.label}. Devis gratuit, réparation téléphone et dépannage PC.`,
    path: '/contact',
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="relative min-h-screen bg-[#36393F] pt-24 md:pt-32 overflow-hidden">
      {/* Orbes de fond */}
      <div className="absolute top-[10%] left-[-5%] w-[40%] h-[40%] bg-[#5865F2] opacity-[0.07] rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute bottom-[10%] right-[-5%] w-[40%] h-[40%] bg-[#5865F2] opacity-[0.07] rounded-full blur-[100px] pointer-events-none" />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">

        {/* HERO */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#5865F2]/20 text-[#5865F2] mb-5">
            <BadgeCheck size={15} />
            <span className="text-sm font-medium">Contactez-nous</span>
          </div>

          <h1 className="text-3xl sm:text-4xl font-bold text-white leading-tight mb-4">
            Une question ? Un projet ?<br />
            <span className="text-[#5865F2]">On vous répond sous 48h.</span>
          </h1>
          <p className="text-gray-400 text-base max-w-xl mx-auto">
            Dépannage informatique à domicile et sur site autour de Viry et Genève · Création de site web dans le monde entier
          </p>
        </motion.div>

        {/* MAIN GRID */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">

          {/* FORMULAIRE — 3 colonnes */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="lg:col-span-3 bg-[#2F3136] rounded-2xl p-6 border border-white/[0.06]"
          >
            <h2 className="text-white font-bold text-base mb-1">Votre demande</h2>
            <p className="text-gray-500 text-xs mb-5">Réponse sous 48h</p>
            <ContactForm />
          </motion.div>

          {/* RÉASSURANCE — 2 colonnes */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="lg:col-span-2 flex flex-col gap-4"
          >
            {/* Bloc Dépannage */}
            <div className="bg-[#2F3136] rounded-2xl p-5 border-2 border-[#5865F2]/30">
              <div className="flex items-center gap-2 mb-3">
                <span className="bg-[#5865F2]/15 text-[#818cf8] rounded-md px-2 py-0.5 text-xs font-semibold border border-[#5865F2]/30">
                  🔧 Dépannage
                </span>
                <span className="text-gray-500 text-xs">Genève &amp; environs</span>
              </div>
              <ul className="space-y-2">
                {['Intervention à domicile ou sur site', 'Diagnostic offert', 'Déplacement inclus · Disponible 7j/7'].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-gray-300">
                    <span className="text-[#5865F2] font-bold mt-0.5 flex-shrink-0">✓</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Bloc Réparation téléphone */}
            <div className="bg-[#2F3136] rounded-2xl p-5 border-2 border-[#5865F2]/30">
              <div className="flex items-center gap-2 mb-3">
                <span className="bg-amber-500/10 text-amber-400 rounded-md px-2 py-0.5 text-xs font-semibold border border-amber-500/20">
                  📱 Réparation téléphone
                </span>
                <span className="text-gray-500 text-xs">Genève &amp; environs</span>
              </div>
              <ul className="space-y-2">
                {[
                  'Écran, batterie, connecteur de charge',
                  'Devis immédiat',
                  'Intervention à domicile',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-gray-300">
                    <span className="text-amber-400 font-bold mt-0.5 flex-shrink-0">✓</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Bloc Création de site */}
            <div className="bg-[#2F3136] rounded-2xl p-5 border-2 border-[#5865F2]/30">
              <div className="flex items-center gap-2 mb-3">
                <span className="bg-green-500/10 text-green-400 rounded-md px-2 py-0.5 text-xs font-semibold border border-green-500/20">
                  🌐 Création de site
                </span>
                <span className="text-gray-500 text-xs">Monde entier</span>
              </div>
              <ul className="space-y-2">
                {[
                  'Sites vitrine à partir de 350€',
                  'Devis gratuit et sans engagement',
                  'Livré en 2 semaines',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-gray-300">
                    <span className="text-green-400 font-bold mt-0.5 flex-shrink-0">✓</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* WhatsApp */}
            <TrackedContactLink channel="whatsapp" service="autre" ctaLocation="contact" 
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 bg-green-500/10 border border-green-500/25 rounded-2xl p-4 hover:bg-green-500/15 transition-colors"
            >
              <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 text-white">
                <WhatsAppIcon />
              </div>
              <div>
                <div className="text-green-400 font-semibold text-sm">Réponse rapide via WhatsApp</div>
                <div className="text-gray-500 text-xs mt-0.5">Pour les urgences · Disponible maintenant</div>
              </div>
            </TrackedContactLink>

          </motion.div>

        </div>
      </div>
    </div>
  );
};

export default ContactPage;
